import { useEffect, useRef } from 'react';
import type Uppy from '@uppy/core';
import type { UniversalMediaUploaderEvent, UppyMeta, UppyBody } from '../../../components/machines/universal-media-uploader-types';
import type { MultiFileEntry } from '../../../components/machines/multi-file-uploader-types';
import type { MediaType } from '../media-type-config';
import MetadataParser, { buildEventMetadata, resolveLegacySourceMetadata } from '../helpers/metadata-parser';
import type { MetadataInput } from '../helpers/metadata-parser';

interface UseMediaUploaderSyncParams {
  editing: boolean;
  uppyInstance: Uppy<UppyMeta, UppyBody> | null | undefined;
  sendEvent: (event: UniversalMediaUploaderEvent) => void;
  files: Map<string, MultiFileEntry>;
  remainingSlots: number;
  allowedFileTypes: string[];
  maxFileSize: number | undefined;
  mediaType: MediaType | undefined;
  onAdd: (
    metadata: Record<string, unknown>,
    uploadedFile: { access_key: string; name?: string },
  ) => void;
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function asStringOrNumber(value: unknown): string | number | undefined {
  return typeof value === 'string' || typeof value === 'number' ? value : undefined;
}

function asNullableStringOrNumber(value: unknown): string | number | null | undefined {
  if (value === null) return null;
  return asStringOrNumber(value);
}

function asExif(value: unknown): MetadataInput['exif'] | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const exif = value as Record<string, unknown>;
  return {
    gps_altitude: asNullableStringOrNumber(exif.gps_altitude),
    gps_dop: asNullableStringOrNumber(exif.gps_dop),
    gps_img_direction: asNullableStringOrNumber(exif.gps_img_direction),
    pixel_x_dimension: asNullableStringOrNumber(exif.pixel_x_dimension),
    pixel_y_dimension: asNullableStringOrNumber(exif.pixel_y_dimension),
    date_time: asString(exif.date_time) ?? null,
  };
}

function asMetadata(value: unknown): MetadataInput['metadata'] | undefined {
  if (!value || typeof value !== 'object') return undefined;
  return value;
}

/**
 * Bridges the universal media uploader machine with the field value model.
 *
 * Handles three concerns that belong together:
 * - Syncing Uppy's maxNumberOfFiles restriction when remainingSlots changes mid-session.
 * - Sending RESET and clearing processed-upload tracking when editing is discarded.
 * - Calling onAdd for each newly completed upload to commit it to the field value.
 */
export default function useMediaUploaderSync({
  editing,
  uppyInstance,
  sendEvent,
  files,
  remainingSlots,
  allowedFileTypes,
  maxFileSize,
  mediaType,
  onAdd,
}: UseMediaUploaderSyncParams): void {
  const processedUploadsRef = useRef<Set<string>>(new Set());
  const prevEditingRef = useRef(editing);
  const metadataParserRef = useRef(new MetadataParser());

  // Sync Uppy's maxNumberOfFiles restriction when remainingSlots changes mid-session.
  // Full restrictions object is passed to avoid Uppy's shallow-merge dropping
  // allowedFileTypes and maxFileSize.
  const prevRemainingSlotsRef = useRef<number | null>(null);
  useEffect(() => {
    if (!uppyInstance) return;

    const next = remainingSlots;

    // Skip on initial mount — restrictions were already set by initialize().
    if (prevRemainingSlotsRef.current === null) {
      prevRemainingSlotsRef.current = next;
      return;
    }
    if (prevRemainingSlotsRef.current === next) return;

    prevRemainingSlotsRef.current = next;
    uppyInstance.setOptions({
      restrictions: {
        allowedFileTypes,
        maxFileSize,
        maxNumberOfFiles: next,
      },
    });
  }, [remainingSlots, allowedFileTypes, maxFileSize, uppyInstance]);

  // Reset uploader state when editing is discarded (editing: true → false).
  // This clears completed/in-progress/error file entries so they don't
  // reappear if the user enters edit mode again.
  useEffect(() => {
    if (prevEditingRef.current && !editing) {
      sendEvent({ type: 'RESET' });
      processedUploadsRef.current.clear();
    }
    prevEditingRef.current = editing;
  }, [editing, sendEvent]);

  useEffect(() => {
    // Skip when not editing — the RESET effect clears processedUploadsRef
    // in the same effect batch, and the state machine hasn't processed
    // RESET yet so files still has entries. Without this guard we'd
    // re-add every completed upload back into the value on discard.
    if (!editing) return;

    files.forEach((entry, fileId) => {
      if (entry.attachmentData && !processedUploadsRef.current.has(fileId)) {
        processedUploadsRef.current.add(fileId);

        const accessKey = entry.attachmentData.id;

        if (accessKey) {
          const sourceMetadata = resolveLegacySourceMetadata(mediaType, entry.attachmentData);
          const fileSize = asStringOrNumber(sourceMetadata.file_size)
            ?? asStringOrNumber(entry.attachmentData.file_size)
            ?? asStringOrNumber(sourceMetadata.size)
            ?? asStringOrNumber(entry.attachmentData.size)
            ?? asStringOrNumber(entry.file.size)
            ?? '';

          const legacyMetadataInput: MetadataInput = {
            access_key: asString(sourceMetadata.access_key) ?? asString(entry.attachmentData.access_key) ?? accessKey,
            file_size: fileSize,
            latitude: asNullableStringOrNumber(sourceMetadata.latitude)
              ?? asNullableStringOrNumber(entry.attachmentData.latitude),
            longitude: asNullableStringOrNumber(sourceMetadata.longitude)
              ?? asNullableStringOrNumber(entry.attachmentData.longitude),
            exif: asExif(sourceMetadata.exif) ?? asExif(entry.attachmentData.exif),
            metadata: asMetadata(sourceMetadata.metadata) ?? asMetadata(entry.attachmentData.metadata),
          };

          const parsedLegacyMetadata = metadataParserRef.current.parse(legacyMetadataInput);
          const metadata = buildEventMetadata(mediaType, accessKey, parsedLegacyMetadata, entry.attachmentData);

          onAdd(
            metadata,
            {
              access_key: accessKey,
              // Only pass name for attachment types — AttachmentValue.addItem uses it as file name.
              // MediaValue.addItem treats the second arg as caption, so passing the file name
              // would incorrectly populate the caption field for photo/video/audio/signature.
              name: mediaType === 'attachment' ? entry.attachmentData.name : undefined,
            },
          );
        }
      }
    });
  }, [editing, files, onAdd, mediaType]);
}
